import { Button, Checkbox, Divider, FormControl, FormLabel, IconButton, Input, Select } from "@chakra-ui/react";
import Modal from "../../components/Modal"
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { getAxiosInstance } from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import axios from "axios";
import { TbTrash } from "react-icons/tb";

interface NewProductModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type MeasurementUnits = {
  id: string,
  name: string,
}

type Product = {
  name: string;
  description: string;
  sku: string;
  minQuanity: number;
  unitMeasurementId: string;
  productWorkPrice: number;
  productProgramingWorkPrice: number | null;
  hasSerialNumber: boolean;
}

const emptyProductWorkPrice: Product = {
  name: "",
  description: "",
  sku: "",
  unitMeasurementId: "",
  minQuanity: 0,
  productWorkPrice: 0,
  productProgramingWorkPrice: null,
  hasSerialNumber: false 
}

const ModalProduct = (props: NewProductModalProps) => {
  const { t } = useTranslation("translations");
  const [measurements, setMeasurements] = useState<MeasurementUnits[]>([]);
  const [products, setProducts] = useState<Product>({ ...emptyProductWorkPrice });
  const [price, setPrice] = useState<number>(0);
  const [links, setLinks] = useState<string[]>([]);

  const unitsOfMeasure = async () => {
    try {
      const res = await getAxiosInstance().get("/UnitsOfMeasure");
      setMeasurements(res.data);
    } catch (error) {
      toast.error("Could not retrieve measurement units", { position: toast.POSITION.BOTTOM_RIGHT });
    }
  }

  const addProduct = async () => {
    try {
      const res = await getAxiosInstance().post("/products", {
        ...products,
        price: {
          name: "-",
          value: price,
          links
        }
      });
      if (res.status === 200) {
        setProducts({ ...emptyProductWorkPrice });
        props.onClose();
      } else {
        toast.error("Something is wrong", { position: toast.POSITION.BOTTOM_RIGHT });
      }

    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(`${error.response?.data}`, { position: toast.POSITION.BOTTOM_RIGHT })
      } else {
        toast.error(`Fields not filled out properly ${error}`, { position: toast.POSITION.BOTTOM_RIGHT });
      }
    }
  }

  const removeLink = (index: number) => {
    let arr = [...links];
    arr.splice(index, 1);
    setLinks(arr);
  }

  const setLinksValue = (index: number, value: string) => {
    let arr = [...links];
    arr[index] = value;
    setLinks(arr);
  }

  const addNewLink = () => {
    setLinks([...links, ""])
  }

  useEffect(() => {
    unitsOfMeasure();
  }, [])

  return (
    <>
      <Modal
        header={t("ADD_PRODUCT")}
        isOpen={props.isOpen}
        onClose={props.onClose}
        secondaryActionName={t("Save")}
        secondaryActionEnabled={true}
        secondaryAction={() => { addProduct() }}
      >
        <div className="space-y-2">
          <FormControl>
            <FormLabel>{t("Name")}</FormLabel>
            <Input
              isRequired
              name='name'
              onChange={(e) => {
                setProducts({
                  ...products,
                  [e.target.name]: e.target.value,
                  sku: e.target.value.toLocaleUpperCase().replaceAll(" ", "_")
                })
              }}
              placeholder='ex: John Doe'
            />
          </FormControl>
          <FormControl>
            <FormLabel>{t("Description")}</FormLabel>
            <Input
              isRequired
              name='description'
              onChange={(e) => {
                setProducts({ ...products, [e.target.name]: e.target.value })
              }}
              placeholder={t("PRODUCT_PLACE_HOLDER_DESCRIPTION")}
            />
          </FormControl>
          <FormControl>
            <FormLabel>{t("SKU")}</FormLabel>
            <Input
              isRequired
              disabled
              name='sku'
              value={products.sku}
              onChange={(e) => {
                setProducts({ ...products, [e.target.name]: e.target.value })
              }}
              placeholder='ex: SKU123456678'
            />
          </FormControl>
          <FormControl>
            <FormLabel>{t("Minimum Quantity")}</FormLabel>
            <Input
              isRequired
              name='minQuantity'
              onChange={(e) => {
                setProducts({ ...products, [e.target.name]: e.target.value })
              }}
              placeholder='ex: 200'
            />
          </FormControl>
          <FormControl>
            <FormLabel>{t("UNIT_OF_MEASUREMENT")}</FormLabel>
            <Select onChange={(e) => { setProducts({ ...products, unitMeasurementId: e.target.value }) }} placeholder='Select option'>
              {
                measurements.map((c: MeasurementUnits, index: number) => {
                  return (
                    <>
                      <option key={index} id={c.id} value={c.id}>  {c.name} </option>
                    </>
                  )
                })
              }
            </Select>
          </FormControl>

          <FormControl className="flex flex-row my-2 mt-4">
            <Checkbox
                isDisabled={false}
                className="mr-[5px]"
                isChecked={products.hasSerialNumber}
                onChange={() => {
                  let tmp = { ...products };
                  tmp.hasSerialNumber = !tmp.hasSerialNumber;
                  setProducts(tmp);
                }}
            />
            <FormLabel className="mt-[5px]">{t("PRODUCT_WITH_SERIAL_NUMBER")}</FormLabel>
          </FormControl>

          <FormControl>
            <FormLabel>{t("PRODUCT_WORK_PRICE_RATE")}</FormLabel>
            <Input
              isRequired
              name="productWorkPrice"
              type="number"
              step="0.01"
              inputMode="numeric"
              placeholder="ex: 200"
              onChange={(e) => setProducts({ ...products, productWorkPrice: +e.target.value})}
            />
          </FormControl>

          <div className="flex flex-row my-2">
            <Checkbox
              isChecked={products.productProgramingWorkPrice !== null }
              onChange={() => setProducts({ ...products, productProgramingWorkPrice: products.productProgramingWorkPrice !== null ? null : 0 })}
            />
            <FormLabel className="ml-2 mt-[6px]"> {t("HAS_PROGRAMING_PRICE")} </FormLabel>
          </div>

          {
            products.productProgramingWorkPrice !== null &&
            <FormControl>
              <FormLabel>{t("PRODUCT_PROGRAMING_WORK_PRICE_RATE")}</FormLabel>
              <Input
                isRequired
                name="productProgramingWorkPrice"
                type="number"
                step="0.01"
                inputMode="numeric"
                placeholder="ex: 0.2"
                onChange={(e) => setProducts({ ...products, productProgramingWorkPrice: +e.target.value })}
              />
            </FormControl>
          }

          <Divider className="m-2"/>

          <FormControl>
            <FormLabel> {t("SELL_VALUE")} </FormLabel>
            <Input
              isRequired
              name='value'
              type="number"
              inputMode="numeric"
              value={price}
              onChange={(e) => setPrice(+e.target.value)}
            />
          </FormControl>
          <FormLabel className="mt-4">{t("Link's")}</FormLabel>
          <Divider color={"teal.200"} />
          {
            links.map((c: any, index: number) =>
              <FormControl className={"block"} key={index}>
                <div className="mt-3 flex items-between">
                  <IconButton
                    onClick={() => removeLink(index)}
                    left={"max"}
                    aria-label='Delete link'
                    className="mr-2"
                    colorScheme="red"
                    icon={<TbTrash />}
                  />
                  <Input
                    isRequired
                    name='link'
                    onChange={(e) => { setLinksValue(index, e.target.value) }}
                    value={c}
                    placeholder={t("Link")}
                  />
                </div>
              </FormControl>
            )
          }
          <Button className="mt-2" onClick={(e) => { addNewLink() }}> {t("Add another link")}</Button>
        </div>
      </Modal>
    </>
  )
}

export default ModalProduct;